import { printHtml, pageMarginBoxes } from '../../utils/printDocument';
import { formatClinicDate } from '../../utils/clinicTime';
import { packLabel, unitsIn } from './packWords';

// The A4 order sent to a supplier: what to send, in their own words and their own packs.
//
// Each line is named the way that supplier prints it on their bills ("COVAM PLUS TAB 10 160 12.5
// 4X7"), with the clinic's own name beneath it, because a booker reading the clinic's names has to
// guess which strength is meant, and a guessed strength is the wrong item on the shelf. Quantities
// are in the supplier's packs, and a liquid's pack is the bottle it arrives in ("Bottle of 60 ml"),
// never the smaller one it is dispensed in. A pack count carries its tablets in a bracket ("10 (20)")
// so the booker does not have to multiply. No prices: the supplier quotes, and the bill that comes
// back is checked against what was delivered, not against this sheet.

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const STYLES = `
  * { box-sizing: border-box; }
  @page { size: A4; margin: 16mm 14mm; ${pageMarginBoxes()} }
  body { font-family: Arial, sans-serif; color: #111; font-size: 12px; margin: 0; }
  .head { text-align: center; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 14px; }
  .name { font-size: 18px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; }
  .urdu { font-size: 15px; direction: rtl; }
  .contact { font-size: 11px; color: #444; }
  h1 { font-size: 16px; margin: 0 0 10px; letter-spacing: 1px; text-transform: uppercase; }
  .meta { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
  .meta div { line-height: 1.6; }
  .meta b { display: inline-block; min-width: 92px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: top; text-align: left; }
  th { background: #f0f0f0; font-size: 11px; text-transform: uppercase; }
  td.n, th.n { width: 34px; text-align: center; }
  td.q, th.q { width: 120px; text-align: right; font-weight: 700; font-size: 13px; white-space: nowrap; }
  .units { font-weight: 400; font-size: 11px; color: #444; }
  td.p, th.p { width: 110px; }
  .ours { font-size: 10px; color: #555; margin-top: 2px; }
  .notes { margin-top: 14px; font-size: 11px; line-height: 1.6; }
  .sign { display: flex; justify-content: space-between; margin-top: 48px; }
  .sign div { width: 40%; border-top: 1px solid #111; padding-top: 4px; font-size: 11px; text-align: center; }
`;

/** The lines an order asks the supplier for: none cancelled, none for nothing. */
export const sheetItems = (order) =>
  (order.items || []).filter((i) => i.status !== 'CANCELLED' && (i.packs || i.quantity));

/** The day on the order: when it was sent, as "25 October 2026". */
export const sheetDate = (order) =>
  formatClinicDate(order.orderDate || new Date().toISOString().slice(0, 10), { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * One order's sheet, for printing or for a PDF. `order` is a MedicineOrder as the API returns it
 * (vendor and items with their medicine); `branding` is brandingAPI.get()'s answer, for the
 * letterhead.
 */
export function orderSheetParts(order, branding) {
  const profile = branding?.profile || {};
  const name = profile.nameEnglish || 'Perveen Aslam Memorial Hospital';
  const nameUrdu = profile.nameUrdu || 'پروین اسلم میموریل ہسپتال';
  const items = sheetItems(order);
  const date = sheetDate(order);

  const rows = items
    .map((i, idx) => {
      const ours = i.medicine?.name || '';
      const theirs = i.vendorItemName || ours;
      const qty = i.packs != null ? i.packs : i.quantity;
      const units = i.packs != null ? unitsIn(i.packs, i.medicine?.supplierBottle, i.unitsPerPack || 1).trim() : '';
      return `<tr>
        <td class="n">${idx + 1}</td>
        <td>${esc(theirs)}${theirs !== ours ? `<div class="ours">${esc(ours)}</div>` : ''}</td>
        <td class="p">${esc(i.packs != null ? packLabel(i.medicine?.supplierBottle, i.unitsPerPack || 1) : 'Units')}</td>
        <td class="q">${esc(qty)}${units ? ` <span class="units">${esc(units)}</span>` : ''}</td>
      </tr>`;
    })
    .join('');

  const bodyHtml = `
    <div class="head">
      <div class="name">${esc(name)}</div>
      <div class="urdu">${esc(nameUrdu)}</div>
      ${profile.address ? `<div class="contact">${esc(profile.address)}</div>` : ''}
      ${profile.phone ? `<div class="contact">Tel: ${esc(profile.phone)}</div>` : ''}
    </div>
    <h1>Purchase Order</h1>
    <div class="meta">
      <div>
        <div><b>To:</b> ${esc(order.vendor?.vendorName || '')}</div>
        ${order.vendor?.contactNumber ? `<div><b>Phone:</b> ${esc(order.vendor.contactNumber)}</div>` : ''}
        ${order.vendor?.address ? `<div><b>Address:</b> ${esc(order.vendor.address)}</div>` : ''}
      </div>
      <div>
        <div><b>Order no:</b> ${esc(order.orderNumber)}</div>
        <div><b>Date:</b> ${esc(date)}</div>
        <div><b>Items:</b> ${items.length}</div>
      </div>
    </div>
    <table>
      <thead><tr><th class="n">#</th><th>Item</th><th class="p">Pack</th><th class="q">Quantity</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="notes">
      Please deliver to ${esc(name)}${profile.address ? `, ${esc(profile.address)}` : ''}.<br />
      Write order number <b>${esc(order.orderNumber)}</b> on your bill, with the batch and expiry of every item.
      If an item is short, send what you have and say so on the bill.
    </div>
    <div class="sign"><div>Ordered by</div><div>Received by</div></div>`;

  return { title: `${order.orderNumber} ${order.vendor?.vendorName || ''}`, styles: STYLES, bodyHtml };
}

/**
 * Prints one order.
 *
 * @returns {boolean} false when the browser blocked the print window
 */
export function printOrderSheet(order, branding) {
  return printHtml(orderSheetParts(order, branding));
}
